import Phaser from 'phaser';
import { AUDIO_KEYS, SfxKey } from '../assets/audioKeys';
import { EXTERNAL_AUDIO } from '../assets/externalAssets';

const MUTE_KEY = 'seism_audio_muted';

const SFX_KEY_MAP: Record<SfxKey, string> = {
  UI_CLICK: AUDIO_KEYS.UI_CLICK,
  INTERACT: AUDIO_KEYS.INTERACT,
  COLLECT: AUDIO_KEYS.COLLECT,
  QUEST: AUDIO_KEYS.QUEST,
  DIALOGUE: AUDIO_KEYS.DIALOGUE,
  DECISION: AUDIO_KEYS.DECISION,
  ERROR: AUDIO_KEYS.ERROR,
  SCORE_UP: AUDIO_KEYS.SCORE_UP,
  TRANSITION: AUDIO_KEYS.TRANSITION,
  EARTHQUAKE: AUDIO_KEYS.EARTHQUAKE,
};

/** Volume/rate default per konteks — lebih natural di gameplay */
const SFX_TUNING: Partial<Record<SfxKey, { volume?: number; rate?: number }>> = {
  UI_CLICK: { volume: 0.5 },
  INTERACT: { volume: 0.72 },
  COLLECT: { volume: 0.88 },
  QUEST: { volume: 0.82 },
  DIALOGUE: { volume: 0.42 },
  DECISION: { volume: 0.78 },
  ERROR: { volume: 0.85 },
  SCORE_UP: { volume: 0.68 },
  TRANSITION: { volume: 0.62 },
  EARTHQUAKE: { volume: 0.92 },
};

export class AudioSystem {
  private muted = false;
  private musicVolume = 0.5;
  private static sfxVolume = 0.7;
  private static game?: Phaser.Game;
  private static audioCtx: AudioContext | null = null;
  private static musicGain: GainNode | null = null;
  private static musicOscillators: OscillatorNode[] = [];
  private static musicRequested = false;
  private static ambientAudio: HTMLAudioElement | null = null;
  private static useFileMusic = true;
  private static earthquakeRumble?: Phaser.Sound.BaseSound;
  private static proceduralRumble: {
    ctx: AudioContext;
    source: AudioBufferSourceNode;
    masterGain: GainNode;
    lfo: OscillatorNode;
    lfoGain: GainNode;
  } | null = null;

  constructor(_scene: Phaser.Scene) {
    this.muted = localStorage.getItem(MUTE_KEY) === 'true';
  }

  static bindGame(game: Phaser.Game): void {
    AudioSystem.game = game;
  }

  private static isMuted(): boolean {
    return localStorage.getItem(MUTE_KEY) === 'true';
  }

  private static ensureCtx(): AudioContext | null {
    if (AudioSystem.isMuted()) return null;
    try {
      if (!AudioSystem.audioCtx) {
        AudioSystem.audioCtx = new AudioContext();
      }
      const ctx = AudioSystem.audioCtx;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
      return ctx;
    } catch {
      return null;
    }
  }

  private static playTone(
    ctx: AudioContext,
    frequency: number,
    startTime: number,
    duration: number,
    volume: number,
    type: OscillatorType = 'sine',
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.001, startTime);
    gain.gain.exponentialRampToValueAtTime(Math.max(volume, 0.001), startTime + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.05);
  }

  private static getActiveScene(): Phaser.Scene | undefined {
    const scenes = AudioSystem.game?.scene.getScenes(true) ?? [];
    return scenes.find((s) => s.scene.isActive());
  }

  /** Putar efek suara sesuai kondisi — file WAV prosedural, fallback Web Audio */
  static playSfx(sfx: SfxKey, config?: { volume?: number; rate?: number }): void {
    if (AudioSystem.isMuted()) return;

    const tuning = SFX_TUNING[sfx] ?? {};
    const key = SFX_KEY_MAP[sfx];
    const volume = (config?.volume ?? tuning.volume ?? 1) * AudioSystem.sfxVolume;
    const rate = config?.rate ?? tuning.rate ?? 1;

    const scenes = AudioSystem.game?.scene.getScenes(true) ?? [];
    for (const scene of scenes) {
      if (scene.cache?.audio?.exists(key)) {
        scene.sound.play(key, { volume, rate });
        return;
      }
    }

    AudioSystem.playProceduralFallback(sfx);
  }

  /** Loop getaran tanah selama simulasi gempa Zona 2 */
  static startEarthquakeRumble(): void {
    if (AudioSystem.isMuted()) return;
    AudioSystem.stopEarthquakeRumble();

    const scene = AudioSystem.getActiveScene();
    if (scene?.cache?.audio?.exists(AUDIO_KEYS.EARTHQUAKE_RUMBLE)) {
      AudioSystem.earthquakeRumble = scene.sound.add(AUDIO_KEYS.EARTHQUAKE_RUMBLE, {
        loop: true,
        volume: 0.58 * AudioSystem.sfxVolume,
      });
      AudioSystem.earthquakeRumble.play();
      return;
    }

    AudioSystem.startProceduralEarthquakeRumble();
  }

  static stopEarthquakeRumble(): void {
    if (AudioSystem.earthquakeRumble) {
      AudioSystem.earthquakeRumble.stop();
      AudioSystem.earthquakeRumble.destroy();
      AudioSystem.earthquakeRumble = undefined;
    }
    AudioSystem.stopProceduralEarthquakeRumble();
  }

  /** Dentuman singkat saat terkena hazard gempa */
  static playEarthquakeImpact(): void {
    AudioSystem.playSfx('EARTHQUAKE', { volume: 0.45, rate: 1.15 });
  }

  private static startProceduralEarthquakeRumble(): void {
    const ctx = AudioSystem.ensureCtx();
    if (!ctx) return;

    const bufferSize = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let brown = 0;
    for (let i = 0; i < bufferSize; i++) {
      brown = (brown + (Math.random() * 2 - 1) * 0.5) * 0.98;
      data[i] = brown * 0.55;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 180;

    const masterGain = ctx.createGain();
    masterGain.gain.value = 0.001;

    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 1.6;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.22 * AudioSystem.sfxVolume;

    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = 34;
    const subGain = ctx.createGain();
    subGain.gain.value = 0.12 * AudioSystem.sfxVolume;

    source.connect(filter);
    filter.connect(masterGain);
    sub.connect(subGain);
    subGain.connect(masterGain);
    masterGain.connect(ctx.destination);
    lfo.connect(lfoGain);
    lfoGain.connect(masterGain.gain);

    const now = ctx.currentTime;
    masterGain.gain.setValueAtTime(0.001, now);
    masterGain.gain.exponentialRampToValueAtTime(
      Math.max(0.25 * AudioSystem.sfxVolume, 0.001),
      now + 0.35,
    );

    source.start();
    lfo.start();
    sub.start();

    AudioSystem.proceduralRumble = { ctx, source, masterGain, lfo, lfoGain };
  }

  private static stopProceduralEarthquakeRumble(): void {
    const rumble = AudioSystem.proceduralRumble;
    if (!rumble) return;

    const { ctx, source, masterGain, lfo } = rumble;
    const now = ctx.currentTime;
    masterGain.gain.cancelScheduledValues(now);
    masterGain.gain.setValueAtTime(masterGain.gain.value, now);
    masterGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

    window.setTimeout(() => {
      try {
        source.stop();
        lfo.stop();
      } catch {
        // already stopped
      }
    }, 450);

    AudioSystem.proceduralRumble = null;
  }

  private static playProceduralFallback(sfx: SfxKey): void {
    const ctx = AudioSystem.ensureCtx();
    if (!ctx) return;
    const t = ctx.currentTime;
    const v = AudioSystem.sfxVolume;

    switch (sfx) {
      case 'UI_CLICK':
        AudioSystem.playTone(ctx, 920, t, 0.05, 0.1 * v, 'sine');
        break;
      case 'INTERACT':
        AudioSystem.playTone(ctx, 280, t, 0.1, 0.14 * v);
        AudioSystem.playTone(ctx, 420, t + 0.02, 0.08, 0.08 * v, 'triangle');
        break;
      case 'COLLECT':
        AudioSystem.playTone(ctx, 880, t, 0.18, 0.16 * v);
        AudioSystem.playTone(ctx, 1320, t + 0.08, 0.14, 0.1 * v);
        break;
      case 'QUEST':
        [523, 659, 784].forEach((f, i) => {
          AudioSystem.playTone(ctx, f, t + i * 0.11, 0.2, 0.12 * v, 'triangle');
        });
        break;
      case 'DIALOGUE':
        AudioSystem.playTone(ctx, 380, t, 0.05, 0.06 * v, 'triangle');
        break;
      case 'DECISION':
        AudioSystem.playTone(ctx, 440, t, 0.1, 0.12 * v);
        AudioSystem.playTone(ctx, 554, t + 0.08, 0.14, 0.13 * v, 'triangle');
        break;
      case 'ERROR':
        AudioSystem.playTone(ctx, 180, t, 0.22, 0.14 * v, 'sawtooth');
        AudioSystem.playTone(ctx, 140, t + 0.1, 0.18, 0.1 * v, 'sawtooth');
        break;
      case 'SCORE_UP':
        AudioSystem.playTone(ctx, 988, t, 0.12, 0.11 * v);
        AudioSystem.playTone(ctx, 1318, t + 0.06, 0.1, 0.08 * v);
        break;
      case 'TRANSITION':
        AudioSystem.playTone(ctx, 420, t, 0.15, 0.1 * v, 'triangle');
        AudioSystem.playTone(ctx, 220, t + 0.1, 0.18, 0.08 * v, 'triangle');
        break;
      case 'EARTHQUAKE':
        [42, 68, 95].forEach((f, i) => {
          AudioSystem.playTone(ctx, f, t + i * 0.06, 0.35 - i * 0.05, 0.16 * v, 'sawtooth');
        });
        break;
      default:
        break;
    }
  }

  static playCollectSfx(): void {
    AudioSystem.playSfx('COLLECT');
  }

  static playVictoryFanfare(): void {
    const ctx = AudioSystem.ensureCtx();
    if (!ctx) return;

    const melody = [
      { freq: 523.25, at: 0, dur: 0.22, vol: 0.18, type: 'sine' as OscillatorType },
      { freq: 659.25, at: 0.14, dur: 0.22, vol: 0.2, type: 'sine' as OscillatorType },
      { freq: 783.99, at: 0.28, dur: 0.22, vol: 0.22, type: 'sine' as OscillatorType },
      { freq: 1046.5, at: 0.42, dur: 0.55, vol: 0.26, type: 'triangle' as OscillatorType },
      { freq: 1318.51, at: 0.58, dur: 0.45, vol: 0.14, type: 'sine' as OscillatorType },
    ];

    const base = ctx.currentTime;
    melody.forEach((note) => {
      AudioSystem.playTone(ctx, note.freq, base + note.at, note.dur, note.vol, note.type);
    });

    [1567.98, 1760, 2093].forEach((freq, i) => {
      AudioSystem.playTone(ctx, freq, base + 0.72 + i * 0.08, 0.18, 0.08, 'sine');
    });
  }

  static playCelebrationChime(): void {
    const ctx = AudioSystem.ensureCtx();
    if (!ctx) return;
    const base = ctx.currentTime;
    [880, 1108.73, 1318.51].forEach((freq, i) => {
      AudioSystem.playTone(ctx, freq, base + i * 0.1, 0.35, 0.12, 'triangle');
    });
  }

  private static stopFileMusic(): void {
    if (!AudioSystem.ambientAudio) return;
    AudioSystem.ambientAudio.pause();
    AudioSystem.ambientAudio.currentTime = 0;
  }

  private static startFileMusic(volume: number): boolean {
    if (!AudioSystem.useFileMusic) return false;
    try {
      if (!AudioSystem.ambientAudio) {
        const audio = new Audio(EXTERNAL_AUDIO.ambient);
        audio.loop = true;
        audio.preload = 'auto';
        AudioSystem.ambientAudio = audio;
      }
      AudioSystem.ambientAudio.volume = Phaser.Math.Clamp(volume * 0.5, 0, 1);
      const playPromise = AudioSystem.ambientAudio.play();
      if (playPromise) {
        playPromise.catch(() => {
          AudioSystem.useFileMusic = false;
          AudioSystem.stopFileMusic();
        });
      }
      return AudioSystem.useFileMusic;
    } catch {
      AudioSystem.useFileMusic = false;
      return false;
    }
  }

  private static stopAmbientMusic(): void {
    AudioSystem.stopFileMusic();
    AudioSystem.musicOscillators.forEach((osc) => {
      try {
        osc.stop();
      } catch {
        // Oscillator sudah berhenti
      }
    });
    AudioSystem.musicOscillators = [];
    AudioSystem.musicGain?.disconnect();
    AudioSystem.musicGain = null;
  }

  private static startAmbientMusic(volume: number): void {
    AudioSystem.stopAmbientMusic();

    if (AudioSystem.startFileMusic(volume)) return;

    const ctx = AudioSystem.ensureCtx();
    if (!ctx) return;

    const masterGain = ctx.createGain();
    masterGain.gain.value = 0.035 * volume;
    masterGain.connect(ctx.destination);
    AudioSystem.musicGain = masterGain;

    const layers: { freq: number; type: OscillatorType; vol: number }[] = [
      { freq: 146.83, type: 'sine', vol: 0.18 },
      { freq: 174.61, type: 'triangle', vol: 0.12 },
      { freq: 220.0, type: 'sine', vol: 0.14 },
      { freq: 261.63, type: 'triangle', vol: 0.1 },
      { freq: 293.66, type: 'sine', vol: 0.08 },
    ];

    layers.forEach(({ freq, type, vol }) => {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.value = vol;
      osc.connect(gain);
      gain.connect(masterGain);
      osc.start();
      AudioSystem.musicOscillators.push(osc);
    });
  }

  static speakCongratulations(text: string): void {
    if (AudioSystem.isMuted()) return;
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'id-ID';
      utterance.rate = 0.92;
      utterance.pitch = 1.05;
      utterance.volume = 1;

      const voices = window.speechSynthesis.getVoices();
      const idVoice = voices.find((v) => v.lang.startsWith('id'));
      if (idVoice) utterance.voice = idVoice;

      window.speechSynthesis.speak(utterance);
    } catch {
      // Browser tanpa dukungan TTS
    }
  }

  playMusic(_key: string): void {
    AudioSystem.musicRequested = true;
    if (this.muted) return;
    AudioSystem.startAmbientMusic(this.musicVolume);
  }

  playSfx(key: string): void {
    if (this.muted) return;
    if (key === 'collect') AudioSystem.playSfx('COLLECT');
    if (key === 'victory') AudioSystem.playVictoryFanfare();
    if (key === 'chime') AudioSystem.playCelebrationChime();
  }

  stopMusic(): void {
    AudioSystem.musicRequested = false;
    AudioSystem.stopAmbientMusic();
  }

  static stopSpeech(): void {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    localStorage.setItem(MUTE_KEY, String(this.muted));
    if (this.muted) {
      AudioSystem.stopEarthquakeRumble();
      AudioSystem.stopAmbientMusic();
      AudioSystem.stopSpeech();
      const scenes = AudioSystem.game?.scene.getScenes(true) ?? [];
      scenes.forEach((s) => s.sound?.stopAll());
    } else if (AudioSystem.musicRequested) {
      AudioSystem.startAmbientMusic(this.musicVolume);
    }
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = Phaser.Math.Clamp(volume, 0, 1);
    if (AudioSystem.ambientAudio) {
      AudioSystem.ambientAudio.volume = Phaser.Math.Clamp(this.musicVolume * 0.5, 0, 1);
    }
    if (AudioSystem.musicGain) {
      AudioSystem.musicGain.gain.value = 0.035 * this.musicVolume;
    }
  }

  setSfxVolume(volume: number): void {
    AudioSystem.sfxVolume = Phaser.Math.Clamp(volume, 0, 1);
  }

  getMusicVolume(): number {
    return this.musicVolume;
  }

  getSfxVolume(): number {
    return AudioSystem.sfxVolume;
  }
}
